import React, { useState, useEffect } from 'react';
import { Avatar, Tooltip } from 'antd';
import { getInitials, getAvatarColor, getAvatarRingColor, avatarInitialsColor, getPresetAvatarSrc } from '../../utils/avatar';
import { getUserAvatar } from '../../reducers/user/resources';

const UserAvatar = ({ 
    name, 
    src, 
    userId, // New prop for refreshing functionality
    size = 'default', 
    className, 
    style = {}, 
    showTooltip = true,
    tooltipTitle,
    shape = 'circle',
    icon,
    onClick,
    onRefresh
}) => {
    // State to manage the source, allowing updates on error
    const [currentSrc, setCurrentSrc] = useState(getPresetAvatarSrc(src));
    const [imgError, setImgError] = useState(false);
    const [isRetrying, setIsRetrying] = useState(false);

    const handleImgError = () => {
        if (!userId || isRetrying) {
            setImgError(true);
            return false;
        }

        setIsRetrying(true);
        
        getUserAvatar(userId)
            .then(res => {
                const newUrl = res.avatar_url || (res.data && res.data.avatar_url);
                const resolved = getPresetAvatarSrc(newUrl);

                if (resolved && resolved !== currentSrc) {
                     setCurrentSrc(resolved);
                     if (onRefresh) {
                         onRefresh(resolved);
                     }
                } else {
                    setImgError(true);
                }
            })
            .catch(err => {
                console.error("Failed to refresh avatar", err);
                setImgError(true);
            });

        return false;
    };

    // Update state when prop changes
    useEffect(() => {
        if (!src && userId) {
             handleImgError();
        } else {
            setCurrentSrc(getPresetAvatarSrc(src));
            setImgError(false);
            setIsRetrying(false);
        }
    }, [src, userId]);

    // Determine content
    let content;

    if (icon) {
        content = (
            <Avatar
                icon={icon}
                size={size}
                className={className}
                shape={shape}
                style={{
                    flexShrink: 0,
                    backgroundColor: '#9bb8c7',
                    border: '1px solid #9bb8c7',
                     ...style
                }}
                onClick={onClick}
            />
        );
    } else if (currentSrc && !imgError) {
        content = (
            <Avatar
                src={currentSrc}
                size={size}
                className={className}
                shape={shape}
                style={{
                    flexShrink: 0,
                    backgroundColor: '#9bb8c7', // Fallback/loading bg
                    border: '1px solid #9bb8c7',
                     ...style
                }}
                onClick={onClick}
                onError={handleImgError}
            >
                {getInitials(name || '')}
            </Avatar>
        );
    } else {
        const initials = getInitials(name || '');
        const backgroundColor = getAvatarColor(name || '');
        const borderColor = getAvatarRingColor(name || '');

        content = (
            <Avatar
                size={size}
                className={className}
                shape={shape}
                style={{
                    flexShrink: 0,
                    backgroundColor: backgroundColor,
                    border: `1px solid ${borderColor}`,
                    color: avatarInitialsColor,
                    fontSize: typeof size === 'number' ? size / 2.2 : undefined, // Heuristic for font size
                    ...style
                }}
                onClick={onClick}
            >
                {initials}
            </Avatar>
        );
    }

    if (showTooltip) {
        return (
            <Tooltip title={tooltipTitle || name}>
                {content}
            </Tooltip>
        );
    }

    return content;
};

export default UserAvatar;
